const en = {
  nav: {
    home: 'Home',
    services: 'Services',
    portfolio: 'Portfolio',
    contact: 'Contact us',
    mobileLabel: 'Menu',
    mobileNote: 'Quick access to sections, contact and language switch on smaller screens.',
  },
  hero: {
    badge: 'Professional web & mobile applications',
    title1: 'We build modern',
    titleGradient: 'apps',
    title2: 'for every platform',
    subtitle:
      'We specialise in React, TypeScript, PWA and React Native / Expo. We deliver fast, responsive and accessible solutions — from business websites to complex mobile applications.',
    ctaPrimary: 'Our services',
    ctaSecondary: 'Get in touch',
    stat0Label: 'years of experience',
    stat1Label: 'projects',
    stat2Label: 'satisfied clients',
    platformLabel: 'A tighter build flow for business websites, PWAs and mobile products.',
    planLabel: 'Delivery rhythm',
    planTitle: 'From brief to release',
    planSubtitle: 'A structured process that keeps design, code and launch moving in the same direction.',
    step0Title: 'Scope and direction',
    step0Desc: 'We clarify the user journey, structure and technical shape before coding starts.',
    step1Title: 'Design and build',
    step1Desc: 'Reusable UI, typed frontend and clean implementation instead of patched-together screens.',
    step2Title: 'Ship and iterate',
    step2Desc: 'Performance checks, QA pass and a clean handoff ready for the next release.',
    metric0Label: 'Platforms',
    metric0Value: 'Web / PWA / Mobile',
    metric1Label: 'Stack',
    metric1Value: 'React + TypeScript',
    metric2Label: 'Process',
    metric2Value: 'Clear and fast',
  },
  services: {
    tag: 'What we do',
    title: 'Our',
    titleGradient: 'services',
    subtitle:
      'From a simple business website to a complex mobile app — we choose the technology that fits your needs.',
    learnMore: 'Learn more',
    web: {
      title: 'Websites',
      desc: 'Modern, fast and responsive websites built with React / Next.js. SEO-friendly and optimised for every device.',
    },
    pwa: {
      title: 'PWA Apps',
      desc: 'Progressive web apps that work offline and can be installed on any device — like native apps, without the App Store.',
    },
    expo: {
      title: 'React Native / Expo',
      desc: 'Mobile apps for iOS and Android from a single codebase. React Native with Expo — fast development, native performance.',
    },
    webapp: {
      title: 'Web Applications',
      desc: 'Advanced web applications — admin panels, dashboards, content management systems and APIs.',
    },
  },
  tech: {
    tag: 'Technologies',
    title: 'Our',
    titleGradient: 'stack',
    subtitle:
      'We work with modern technologies that ensure performance, scalability and great developer experience.',
    leadLabel: 'How we build',
    leadTitle: 'A compact stack, chosen by product type.',
    leadSubtitle:
      'Instead of chasing every trend, we use a small set of tools that keeps design, engineering and delivery aligned.',
    group0Title: 'Frontend surfaces',
    group0Desc: 'For websites, landing pages and product interfaces that need to feel fast and clear.',
    group1Title: 'Apps and behaviour',
    group1Desc: 'For mobile delivery, polished UI systems and richer user flows across platforms.',
    group2Title: 'Data and shipping',
    group2Desc: 'For APIs, integrations and the release workflow that keeps projects maintainable.',
  },
  portfolio: {
    tag: 'Portfolio',
    title: 'Our',
    titleGradient: 'work',
    subtitle:
      'Sample projects from various fields — websites, PWA, mobile apps and web systems.',
    cta: 'Discuss your project',
    p0: { title: 'Online shop', desc: 'Full e-commerce in Next.js with payments, cart and admin panel.' },
    p1: { title: 'Fitness app', desc: 'Cross-platform React Native / Expo — workouts, progress tracking and push notifications.' },
    p2: { title: 'Analytics dashboard', desc: 'Web app for real-time data visualisation with WebSocket.' },
    p3: { title: 'Real estate website', desc: 'Responsive website with listing search, map and filtering.' },
    p4: { title: 'PWA messenger', desc: 'Progressive chat app with offline mode and push notifications.' },
    p5: { title: 'Restaurant app', desc: 'Online ordering system — React Expo on iOS/Android + web panel.' },
  },
  sales: {
    tag: 'How projects move',
    title: 'A clearer',
    titleGradient: 'commercial layer',
    subtitle:
      'A straightforward structure around scope, trust and next steps, so the site sells the work instead of only showing visuals.',
    asideLabel: 'Before the first sprint',
    asideTitle: 'Clear process, cleaner expectations.',
    asideText:
      'The goal is to make contact easier, frame the offer faster and show what working together actually looks like.',
    process0Title: 'Intro call',
    process0Desc: 'We align on the goal, current blockers and what kind of product you actually need.',
    process1Title: 'Scope and estimate',
    process1Desc: 'You get a clearer structure, sensible roadmap and a technical direction before build starts.',
    process2Title: 'Build and feedback',
    process2Desc: 'The project moves in visible stages, with review points instead of one opaque final drop.',
    process3Title: 'Launch and next step',
    process3Desc: 'We finish with release readiness, polish and a view of what should happen after version one.',
    metric0Label: 'Response window',
    metric0Value: 'Usually within 24 hours',
    metric1Label: 'Typical scope',
    metric1Value: 'Websites, PWAs and product MVPs',
    metric2Label: 'Delivery model',
    metric2Value: 'Remote-first and milestone-based',
    metric3Label: 'Stack logic',
    metric3Value: 'One consistent stack across surfaces',
    faqTitle: 'Common questions before we start',
    faq0Question: 'Do you help define scope if the brief is still rough?',
    faq0Answer: 'Yes. Early conversations usually focus on shaping the right scope before discussing detailed implementation.',
    faq1Question: 'Can a simple website grow into a larger app later?',
    faq1Answer: 'Yes. The stack and structure are chosen so a business website can evolve into a PWA or product surface without restarting from zero.',
    faq2Question: 'Do you work only locally or also remotely?',
    faq2Answer: 'Remote collaboration is the default. That keeps delivery simple while still leaving room for structured feedback and milestone reviews.',
    faq3Question: 'What is usually needed to start?',
    faq3Answer: 'A rough business goal, a few example references and a sense of timing are enough to start the first scoping conversation.',
  },
  contact: {
    tag: 'Contact',
    title: "Let's talk about your",
    titleGradient: 'project',
    subtitle:
      'Got an idea for a website, PWA or mobile app? Write to us — we respond within 24 hours.',
    briefLabel: 'Project brief',
    briefTitle: 'Tell us what you want to build',
    briefSubtitle: 'A few sentences are enough to start the conversation and shape the right scope.',
    card0Label: 'Response',
    card0Value: 'Usually within 24 hours',
    card1Label: 'Best fit',
    card1Value: 'Business websites, PWAs and product MVPs',
    card2Label: 'Workflow',
    card2Value: 'Remote-first with clear milestones',
    nameLabel: 'Full name',
    namePlaceholder: 'John Doe',
    emailLabel: 'Email address',
    emailPlaceholder: 'john@company.com',
    subjectLabel: 'Subject',
    subjectPlaceholder: 'e.g. I want to order a PWA app',
    messageLabel: 'Message',
    messagePlaceholder: 'Describe your project...',
    sending: 'Sending...',
    send: 'Send message',
    success: 'Message sent! We will get back to you soon.',
    emailLabel2: 'Email',
    phoneLabel: 'Phone',
    locationLabel: 'Location',
    locationValue: 'Northern Ireland / remote work',
  },
  footer: {
    desc: 'We build modern websites, PWA applications and mobile apps in React Expo. Professionally and on time.',
    navTitle: 'Navigation',
    servicesTitle: 'Services',
    techTitle: 'Technologies',
    navHome: 'Home',
    navServices: 'Services',
    navPortfolio: 'Portfolio',
    navContact: 'Contact',
    svcWeb: 'Websites',
    svcPwa: 'PWA Apps',
    svcExpo: 'React Native / Expo',
    svcWebapp: 'Web Applications',
    copyright: 'All rights reserved.',
    privacy: 'Privacy Policy',
    terms: 'Terms of Service',
  },
  servicesPage: {
    tag: 'Services',
    title: 'What do',
    titleGradient: 'we do',
    subtitle: 'Complete offer — from a business website to an advanced mobile and web application.',
    leadLabel: 'Offer structure',
    leadTitle: 'Four routes, one delivery logic.',
    leadText: 'Each offer block answers a different product need, but the process stays consistent from planning to launch.',
    heroMetric0Label: 'Best for',
    heroMetric0Value: 'Business sites and MVPs',
    heroMetric1Label: 'Platforms',
    heroMetric1Value: 'Web / PWA / Mobile',
    heroMetric2Label: 'Approach',
    heroMetric2Value: 'Lean, fast and typed',
    s0: {
      tag: 'Web Development',
      title: 'Websites & web applications',
      desc: 'We build modern, fast and fully responsive websites and web apps. We use React, Next.js and TypeScript to deliver high-quality code — with SEO, accessibility and performance as priorities.',
    },
    s1: {
      tag: 'Progressive Web Apps',
      title: 'PWA Applications',
      desc: 'Progressive web apps combine the best of websites with the capabilities of native apps. They work offline, can be installed on a device and send push notifications — without the App Store.',
    },
    s2: {
      tag: 'React Native & Expo',
      title: 'Mobile applications',
      desc: 'We build native apps for iOS and Android from a single codebase using React Native with Expo. One team, one codebase — two platforms. Expo SDK speeds up development and enables OTA updates.',
    },
    s3: {
      tag: 'Web Applications',
      title: 'Systems & admin panels',
      desc: 'Advanced web applications — analytics dashboards, CRM systems, admin panels and APIs. Architecture based on React, TypeScript and RESTful/GraphQL backend.',
    },
  },
  portfolioPage: {
    tag: 'Portfolio',
    title: 'Our',
    titleGradient: 'work',
    subtitle: 'Sample projects from various fields — websites, PWA, mobile apps and web systems.',
    leadLabel: 'Selected work',
    leadTitle: 'Three stronger examples instead of a wall of cards.',
    leadText: 'The focus is on what the product needed, how it was shaped and what type of delivery made sense.',
    metric0Label: 'Formats',
    metric0Value: 'Web / PWA / Mobile',
    metric1Label: 'Focus',
    metric1Value: 'Usable flows and scalable UI',
    metric2Label: 'Lens',
    metric2Value: 'Problem, outcome and delivery',
  },
  contactPage: {
    tag: 'Contact',
    title: 'Write to',
    titleGradient: 'us',
    subtitle: 'We respond within 24 hours. We would love to talk about your project.',
    leadLabel: 'Starting point',
    leadTitle: 'You do not need a perfect brief.',
    leadText: 'A short description of the idea, business goal or current problem is enough to start shaping the right project.',
    metric0Label: 'First reply',
    metric0Value: 'Usually within one day',
    metric1Label: 'Best topics',
    metric1Value: 'Web, PWA and product MVPs',
    metric2Label: 'Work mode',
    metric2Value: 'Remote and milestone-based',
  },
} as const;

export default en;
